import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";
import Staff from "@/components/Staff";
import Harp from "@/components/Harp";
import LetterStrip from "@/components/LetterStrip";
import { unlockAudio, playHarp } from "@/lib/synth";
import { playSequenceAudio, playEventsExact } from "@/lib/playEngine";
import { voiceForPreset, PRESETS } from "@/lib/presets";
import { buildStaffAndAudio, isHebrewComposition } from "@/lib/compositionRender";
import { measureSequence } from "@/lib/measures";
import { noteflightLaunchUrl } from "@/lib/musicXmlExport";
import { alefNoteForMode, ROOTS, SCALES, DEFAULT_SCALE, HEBREW_BASE_LETTERS } from "@/lib/hebrewCryptogram";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  try {
    return new Date(d).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (e) {
    return "";
  }
}

export default function SharedComposition() {
  const { id } = useParams();
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [harpLit, setHarpLit] = useState(() => new Set());
  const harpRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/compositions/${id}`);
        if (!cancelled) setComp(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? "not_found" : "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handlePlay = async () => {
    if (!comp || playing) return;
    unlockAudio();
    setPlaying(true);
    setActiveIdx(-1);
    const { audioEvents, mode } = buildStaffAndAudio(comp);
    const isHarp = comp.instrument === "harp";
    if (mode === "events") {
      let i = 0;
      await playEventsExact(audioEvents, {
        voice: isHarp ? "harp" : voiceForPreset(comp.preset_key || "meditation"),
        onNote: () => {
          const idx = i;
          setActiveIdx(idx);
          i += 1;
        },
      });
    } else if (isHarp) {
      // Written on the harp: replay it on the harp, plucking as it goes.
      const rung = new Set();
      await playSequenceAudio(audioEvents, {
        presetKey: comp.preset_key || "meditation",
        voice: "harp",
        pedal: false,
        onTick: (idx, entry) => {
          setActiveIdx(idx);
          const letterIdx = HEBREW_BASE_LETTERS.indexOf(entry.baseLetter);
          if (letterIdx < 0) return;
          rung.add(letterIdx);
          setHarpLit(new Set(rung));
          harpRef.current?.pluck(letterIdx);
        },
      });
    } else if (comp.measured) {
      // Saved in measured mode: replay the same bars, rests and all.
      let noteIdx = -1;
      await playSequenceAudio(measureSequence(audioEvents).steps, {
        presetKey: comp.preset_key || "meditation",
        measured: true,
        pedal: comp.pedal !== undefined ? comp.pedal : true,
        onTick: (_idx, step) => {
          if (step.rest) return;
          noteIdx += 1;
          setActiveIdx(noteIdx);
        },
      });
    } else {
      await playSequenceAudio(audioEvents, {
        presetKey: comp.preset_key || "meditation",
        pedal: comp.pedal !== undefined ? comp.pedal : true,
        onTick: (idx) => setActiveIdx(idx),
      });
    }
    setActiveIdx(-1);
    setPlaying(false);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch {
      toast.message("Copy this link", { description: window.location.href, duration: 8000 });
    }
  };

  return (
    <div
      className="min-h-screen w-full"
      style={{
        backgroundColor: "#FAF8F5",
        color: "#1A1523",
        fontFamily: "'Courier Prime', monospace",
      }}
    >
      <Toaster position="bottom-right" richColors />

      <header className="max-w-[1100px] mx-auto px-6 pt-10 pb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <Link
            to="/"
            data-testid="shared-back-link"
            className="text-xs uppercase tracking-[0.18em] underline underline-offset-4 hover:text-[#E86F4A]"
            style={{ color: "#5C5268" }}
          >
            &lsaquo; Alephonics
          </Link>
          <h1
            className="mt-2 text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            data-testid="shared-title"
          >
            Shared score<span style={{ color: "#E86F4A" }}>.</span>
          </h1>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 pb-20">
        {loading && (
          <p className="text-sm" style={{ color: "#5C5268" }} data-testid="shared-loading">
            Loading score…
          </p>
        )}

        {!loading && error === "not_found" && (
          <div
            className="rounded-md border p-10 text-center"
            style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}
            data-testid="shared-not-found"
          >
            <p
              className="text-2xl mb-2"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              This score isn&apos;t here.
            </p>
            <p className="text-sm" style={{ color: "#5C5268" }}>
              It may have been deleted, or the link is incorrect.
            </p>
            <Link
              to="/"
              className="inline-block mt-4 text-xs uppercase tracking-[0.18em] underline underline-offset-4 hover:text-[#E86F4A]"
            >
              Back to The Alephonics App &rsaquo;
            </Link>
          </div>
        )}

        {!loading && error === "error" && (
          <p className="text-sm" style={{ color: "#EBE3D5" }}>
            Couldn&apos;t load that score.
          </p>
        )}

        {!loading && comp && (() => {
          const { staffEntries } = buildStaffAndAudio(comp);
          const isHebrew = isHebrewComposition(comp);
          const rootLabel = isHebrew
            ? (ROOTS.find((r) => r.value === (comp.scale_root || "D"))?.label || comp.scale_root || "D")
            : null;
          const alefNote = isHebrew
            ? alefNoteForMode(comp.scale_root || "D", comp.scale_mode ?? 0, comp.scale_family || DEFAULT_SCALE)
            : null;
          const openLink = `/app/${comp.instrument === "harp" ? "harp" : "hebrew"}?words=${encodeURIComponent(comp.words.join(" "))}${comp.scale_root ? `&root=${encodeURIComponent(comp.scale_root)}` : ""}${Number.isFinite(comp.scale_mode) ? `&mode=${comp.scale_mode}` : ""}${comp.scale_family ? `&scale=${encodeURIComponent(comp.scale_family)}` : ""}${comp.measured ? "&measured=1" : ""}${comp.condensed ? "&condensed=1" : ""}`;
          const isRemix =
            !!(comp.remix_taps && comp.remix_taps.length > 0) ||
            !!(comp.remix_events && comp.remix_events.length > 0);
          const measuredSteps =
            comp.measured && !isRemix ? measureSequence(staffEntries).steps : null;
          // Verbatim events carry wordIdx "event-N"; the strip wants the real
          // word grouping so a plucked run still reads as words.
          const stripEntries = staffEntries.map((e) => ({
            ...e,
            wordIdx: e.displayWordIdx ?? e.wordIdx,
          }));

          return (
            <article
              className="rounded-md border p-5 sm:p-7"
              style={{ borderColor: "#EBE3D5", backgroundColor: "#FFFBF2" }}
              data-testid="shared-card"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <h2
                    className="text-3xl"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    data-testid="shared-comp-title"
                  >
                    {comp.title}
                  </h2>
                  <p className="text-xs mt-1" style={{ color: "#5C5268" }}>
                    {comp.words.join(" · ")} &nbsp;—&nbsp; {formatDate(comp.created_at)}
                  </p>
                  {isHebrew && (
                    <p
                      className="mt-2 text-[10px] uppercase tracking-[0.16em] inline-block px-2 py-0.5 rounded-full border"
                      style={{
                        borderColor: "#FFFFFF",
                        backgroundColor: "transparent",
                        color: "#5C5268",
                      }}
                      data-testid="shared-scale-badge"
                    >
                      {rootLabel} {SCALES.find((s) => s.value === (comp.scale_family || DEFAULT_SCALE))?.label} · aleph on {alefNote}
                    </p>
                  )}
                  <p
                    className="mt-2 ml-2 text-[10px] uppercase tracking-[0.16em] inline-block px-2 py-0.5 rounded-full border"
                    style={{ borderColor: "#D99B26", color: "#8A6514" }}
                    data-testid="shared-feel-badge"
                  >
                    {PRESETS[comp.preset_key]?.label || "Meditative"}
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    data-testid="shared-play-button"
                    onClick={handlePlay}
                    disabled={playing}
                    className="rounded-full text-white"
                    style={{
                      backgroundColor: "#1B6C7A",
                      fontFamily: "'Courier Prime', monospace",
                      letterSpacing: "0.16em",
                    }}
                  >
                    {playing ? "Playing…" : "Play"}
                  </Button>
                  <Link
                    to={openLink}
                    data-testid="shared-open-link"
                    className="inline-flex items-center rounded-full border border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5 px-4 h-9 text-sm"
                    style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.16em" }}
                  >
                    Open in the app
                  </Link>
                  <Button
                    data-testid="shared-copy-link"
                    onClick={handleCopyLink}
                    variant="outline"
                    className="rounded-full border-[#1A1523] text-[#1A1523] hover:bg-[#1A1523]/5"
                    style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.16em" }}
                  >
                    Copy link
                  </Button>
                  {comp.has_score && (
                    <a
                      href={noteflightLaunchUrl(`${API}/compositions/${comp.id}/score.musicxml`)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="shared-noteflight-link"
                      className="inline-flex items-center rounded-full border border-[#D6CBB8] text-[#1A1523] hover:bg-[#F3EFE8] px-4 h-9 text-sm"
                      style={{ fontFamily: "'Courier Prime', monospace", letterSpacing: "0.16em" }}
                    >
                      Open in Noteflight
                    </a>
                  )}
                </div>
              </div>
              {comp.instrument === "harp" ? (
                <>
                  <LetterStrip theme="light"
                    sequence={stripEntries}
                    playedKeys={
                      new Set(
                        stripEntries
                          .slice(0, activeIdx + 1)
                          .map((e) => `${e.wordIdx}:${e.charIdx}`)
                      )
                    }
                    activeKey={
                      playing && activeIdx >= 0 && stripEntries[activeIdx]
                        ? `${stripEntries[activeIdx].wordIdx}:${stripEntries[activeIdx].charIdx}`
                        : null
                    }
                  />
                  <div className="mt-4">
                    <Harp theme="light"
                      ref={harpRef}
                      scaleOptions={{
                        root: comp.scale_root || "G",
                        modeIdx: comp.scale_mode ?? 0,
                        scale: comp.scale_family || DEFAULT_SCALE,
                      }}
                      lit={harpLit}
                      onPluck={(s) => {
                        unlockAudio();
                        playHarp(s.pitch, 2.4, 0.7);
                      }}
                    />
                  </div>
                  <p className="mt-3 text-center text-xs" style={{ color: "#8C8198" }}>
                    Plucked on the harp — play it back, or try the strings yourself.
                  </p>
                </>
              ) : (
                <Staff theme="light" droppedEntries={staffEntries} activeIndex={playing ? activeIdx : -1} measuredSteps={measuredSteps} baseStepMs={800} />
              )}
            </article>
          );
        })()}
      </main>
    </div>
  );
}
